import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { ICartState, ICartProduct, ICartProducts } from "interfaces/redux/reducers";

import { CARTS_SLICE } from "./sliceNames";
import { IMerchantCartProduct } from "interfaces/Carts/carts.interface";

const initialState: ICartState = {
  cart_products: {},
  is_carts_loading: true,
  cart_merchant_products: {},
};

interface IUpdateCartsReducer {
  cart_product: ICartProduct;
}

interface IHydrateCartsFromLocalDBReducer {
  cart_products: ICartProducts;
  is_carts_loading?: boolean;
}

const cartsSlice = createSlice({
  name: CARTS_SLICE,
  initialState,
  reducers: {
    updateCartsReducer: (state, action: PayloadAction<IUpdateCartsReducer>) => {
      const cartProduct = action.payload.cart_product;
      const productId = cartProduct.id;

      if (!!action.payload.cart_product.quantity) {
        return {
          ...state,
          cart_products: { ...state.cart_products, [productId]: cartProduct },
        };
      }

      const newCartProducts = { ...state.cart_products };
      delete newCartProducts?.[productId];

      return {
        ...state,
        cart_products: newCartProducts,
      };
    },
    removeProductReducer: (state, action: PayloadAction<{ productId: string }>) => {
      const newCartProducts = { ...state.cart_products };
      delete newCartProducts?.[action?.payload?.productId];

      return {
        ...state,
        cart_products: newCartProducts,
      };
    },
    hydrateCartsFromLocalDBReducer: (state, action: PayloadAction<IHydrateCartsFromLocalDBReducer>) => {
      const localDBCartProducts = action.payload.cart_products;
      const loadingState = action.payload.is_carts_loading;

      return {
        ...state,
        cart_products: localDBCartProducts,
        is_carts_loading: !!loadingState,
      };
    },
    bulkRemoveProductsCartReducer: (state, action: PayloadAction<{ productIds: string[] }>) => {
      const newCartProducts = { ...state.cart_products };

      action.payload.productIds.forEach(productId => {
        delete newCartProducts?.[productId];
      });

      return {
        ...state,
        cart_products: newCartProducts,
      };
    },

    // new logics for carts
    upsertCartMerchantProduct: (state, action: PayloadAction<{ merchantCartProduct: IMerchantCartProduct }>) => {
      if (!action.payload.merchantCartProduct.quantity) {
        const newCartMerchantProducts = { ...state.cart_merchant_products };
        delete newCartMerchantProducts[action.payload.merchantCartProduct.productInfo.id];

        return {
          ...state,
          cart_merchant_products: newCartMerchantProducts,
        };
      }

      return {
        ...state,
        cart_merchant_products: {
          ...state.cart_merchant_products,
          [action.payload.merchantCartProduct.productInfo.id]: action.payload.merchantCartProduct,
        },
      };
    },
    removeCartMerchantProducts: (state, action: PayloadAction<{ productIDs: string[] }>) => {
      const newMerchantCartProducts = { ...state.cart_merchant_products };

      action.payload.productIDs.forEach(productID => {
        if (newMerchantCartProducts?.[productID]) {
          delete newMerchantCartProducts?.[productID];
        }
      });

      return {
        ...state,
        cart_merchant_products: newMerchantCartProducts,
      };
    },
    hydrateCartsMerchantProductsFromLocalDBReducer: (
      state,
      action: PayloadAction<{
        cartMerchantProducts: {
          [product_id: string]: IMerchantCartProduct;
        };
        is_carts_loading: boolean;
      }>,
    ) => {
      const loadingState = action.payload.is_carts_loading;
      const localDBMerchantCartProducts = action.payload.cartMerchantProducts;

      return {
        ...state,
        is_carts_loading: !!loadingState,
        cart_merchant_products: localDBMerchantCartProducts,
      };
    },
    // new logics for carts

    resetCartsReducer: () => {
      return initialState;
    },
  },
});

export const {
  resetCartsReducer: actionResetCartsReducer,
  updateCartsReducer: actionUpdateCartsReducer,
  hydrateCartsFromLocalDBReducer: actionHydrateCartsFromLocalDBReducer,
  removeProductReducer: actionRemoveProductReducer,
  bulkRemoveProductsCartReducer: actionBulkRemoveProductsInCartReducer,
  upsertCartMerchantProduct: actionUpsertCartMerchantProduct,
  removeCartMerchantProducts: actionRemoveCartMerchantProducts,
  hydrateCartsMerchantProductsFromLocalDBReducer: actionHydrateCartsMerchantProductsFromLocalDBReducer,
} = cartsSlice.actions;

export default cartsSlice.reducer;
